import {
  projectVisibleConversationMessages,
  type OpenCloudUIMessage,
} from "@opencloud/agent-protocol";
import { agentBrand } from "./brand.js";
import type {
  AgentRelease,
  ConversationMessage,
  ConversationOutcome,
  ConversationRun,
  ChatSnapshot as ConversationSnapshot,
  GateEvidence,
  HarnessOption,
  ReviewEvidence,
} from "./types.js";

const SETTLED_RUN_STATES = new Set(["idle", "completed", "failed", "cancelled"]);
const LIVE_RELEASE_STATES = new Set(["active", "superseded", "rolled_back"]);

export interface AgentRuntimeSummary {
  harness: string;
  model: string;
  effort: string;
}

export interface RunFailurePresentation {
  eyebrow: string;
  title: string;
}

export function liveDeployment(
  snapshot: ConversationSnapshot,
): NonNullable<
  NonNullable<ConversationSnapshot["deployment"]>["activeDeployment"]
> | null {
  const truth = snapshot.deployment;
  const active = truth?.activeDeployment;
  return truth?.appState === "active" &&
    truth.canonicalUrl !== null &&
    active?.state === "active"
    ? active
    : null;
}

/**
 * Reconciles only terminal copy whose exact root owns the current activation.
 * Agent lifecycle fields remain unchanged; deployment truth is rendered by a
 * separate component.
 */
export function deploymentAwareSnapshot(
  snapshot: ConversationSnapshot,
): ConversationSnapshot {
  const active = liveDeployment(snapshot);
  const rootRunId = active?.activatedByAgentRootRunId;
  if (!rootRunId) return snapshot;
  const run = snapshot.runs.find(({ id }) => id === rootRunId);
  if (!run || (run.status !== "failed" && run.status !== "cancelled")) {
    return snapshot;
  }
  const summary =
    run.status === "failed"
      ? "The Agent turn ended unexpectedly after the app became live. The current deployment remains active."
      : "The Agent turn stopped after the app became live. The current deployment remains active.";
  return {
    ...snapshot,
    ...(snapshot.outcomes
      ? {
          outcomes: snapshot.outcomes.map((outcome) =>
            outcome.runId === rootRunId &&
            (outcome.status === "failed" || outcome.status === "cancelled")
              ? { ...outcome, summary }
              : outcome,
          ),
        }
      : {}),
    ...(snapshot.uiMessages
      ? {
          uiMessages: snapshot.uiMessages.map((message) =>
            deploymentAwareMessage(message, rootRunId, summary),
          ),
        }
      : {}),
  };
}

function deploymentAwareMessage(
  message: OpenCloudUIMessage,
  rootRunId: string,
  summary: string,
): OpenCloudUIMessage {
  return {
    ...message,
    parts: message.parts.map((part) =>
      part.type === "data-terminal" &&
      part.data.runId === rootRunId &&
      (part.data.status === "failed" || part.data.status === "cancelled")
        ? { ...part, data: { ...part.data, summary } }
        : part,
    ),
  };
}

export function runFailurePresentation(
  run: ConversationRun | null | undefined,
  summary?: string,
): RunFailurePresentation {
  const errorClass = run?.terminalErrorClass ?? "";
  if (
    /publishing reused a version name|version name that is already live/i.test(
      summary ?? "",
    )
  ) {
    return {
      eyebrow: "Publishing needs a new version name",
      title: "The current live app is unchanged",
    };
  }
  if (
    errorClass === "runtime_lost_without_checkpoint" ||
    errorClass === "callback_unavailable"
  ) {
    return {
      eyebrow: "Build interrupted",
      title: "Your workspace was preserved",
    };
  }
  if (errorClass === "codex_background_command_cleanup_failed") {
    return {
      eyebrow: "Command cleanup needs attention",
      title: "A background command's exit could not be confirmed",
    };
  }
  if (
    errorClass.includes("release") ||
    errorClass.includes("promotion") ||
    run?.phase === "deploying"
  ) {
    return {
      eyebrow: "Publishing needs attention",
      title: "The verified draft is saved",
    };
  }
  if (
    errorClass.includes("evidence") ||
    errorClass.includes("verification") ||
    errorClass.includes("outcome") ||
    run?.phase === "testing" ||
    run?.phase === "verifying"
  ) {
    return {
      eyebrow: "Checks need attention",
      title: "The draft needs another check",
    };
  }
  return {
    eyebrow: "Agent turn stopped",
    title: "You can continue in this conversation",
  };
}

/** Presents user-visible runtime policy without exposing legacy client choices. */
export function agentRuntimeSummary({
  thread,
  harnesses,
}: {
  thread: ConversationSnapshot["thread"];
  harnesses: readonly HarnessOption[];
}): AgentRuntimeSummary {
  return {
    harness:
      harnesses.find((candidate) => candidate.version === thread.harnessVersion)
        ?.label ?? thread.harnessVersion,
    model: "Automatic",
    effort: "Automatic",
  };
}

export interface ReleaseCheckSummary {
  tone: "passed" | "attention" | "not_needed" | "unavailable";
  label: string;
  detail: string;
}

/**
 * Gives release evidence a useful first line, including an honest state for
 * historical releases whose detailed checks were not retained in the agent
 * conversation.
 */
export function releaseCheckSummary(
  reviews: readonly ReviewEvidence[],
  gates: readonly GateEvidence[],
): ReleaseCheckSummary {
  const passed =
    reviews.filter((review) => review.status === "passed").length +
    gates.filter((gate) => gate.status === "passed").length;
  const attention =
    reviews.filter((review) => review.status === "needs_work").length +
    gates.filter(
      (gate) => gate.status === "failed" || gate.status === "not_tested",
    ).length;
  const notNeeded =
    reviews.filter((review) => review.status === "not_needed").length +
    gates.filter((gate) => gate.status === "not_applicable").length;

  if (attention > 0) {
    return {
      tone: "attention",
      label: `${attention} ${plural(attention, "check needs", "checks need")} attention`,
      detail:
        passed > 0
          ? `${passed} other recorded ${plural(passed, "check passed", "checks passed")}.`
          : "The recorded results explain what still needs attention.",
    };
  }
  if (passed > 0) {
    return {
      tone: "passed",
      label: `${passed} ${plural(passed, "check passed", "checks passed")}`,
      detail:
        notNeeded > 0
          ? `${notNeeded} other ${plural(notNeeded, "check did", "checks did")} not apply.`
          : "Every recorded check cleared.",
    };
  }
  if (notNeeded > 0) {
    return {
      tone: "not_needed",
      label: "No additional checks were needed",
      detail: "The recorded checks did not apply to this change.",
    };
  }
  return {
    tone: "unavailable",
    label: "Detailed check results aren’t available",
    detail:
      "This release has no detailed verification record in this conversation.",
  };
}

/**
 * Produces a short, human app identity from a build request. This is deliberately
 * deterministic: creating the app must not wait for a second model call, and the
 * resulting name is also the source of the stable app-address prefix.
 */
export function deriveAppName(description: string): string {
  const normalized = description
    .replace(/[`*_#>\[\]()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return "New App";

  const explicitlyNamed = normalized
    .match(/\b(?:called|named)\s+["“']?([^"”'.,!?]{2,60})/i)?.[1]
    ?.split(/\s+(?:for|that|which|with|where)\s+/i, 1)[0];
  if (explicitlyNamed) return title(shortProductPhrase(explicitlyNamed));

  let request = normalized;
  const leadIns = [
    /^(?:please\s+)?(?:can|could|would)\s+you\s+/i,
    /^(?:please\s+)?(?:build|create|make|design|develop|set up)\s+/i,
    /^i\s+(?:want|need|would like)\s+(?:you\s+)?(?:to\s+)?/i,
  ];
  for (let pass = 0; pass < 3; pass += 1) {
    const previous = request;
    for (const leadIn of leadIns) request = request.replace(leadIn, "");
    request = request.replace(/^(?:a|an|the)\s+/i, "");
    if (request === previous) break;
  }

  const firstThought = request.split(/[.!?\n]/, 1)[0]?.trim() || request;
  const genericPurpose = firstThought.match(
    /^(?:app|application|website|tool|platform)\s+(?:for|to)\s+(.+)$/i,
  )?.[1];
  let phrase = genericPurpose ?? firstThought;

  const action = phrase.match(
    /\b(track|manage|organize|schedule)\s+(.+?)(?=\s+(?:for|with|that|which|where|so)\b|$)/i,
  );
  if (action) {
    const object = shortProductPhrase(action[2] ?? "");
    const noun = {
      track: "Tracker",
      manage: "Manager",
      organize: "Organizer",
      schedule: "Scheduler",
    }[action[1]?.toLowerCase() as "track" | "manage" | "organize" | "schedule"];
    return title(`${object} ${noun}`);
  }

  const boundary = phrase.search(
    /\s+(?:for|that|which|where|with|so that)\s+/i,
  );
  if (boundary > 0) phrase = phrase.slice(0, boundary);
  phrase = shortProductPhrase(phrase)
    .replace(/\bplanning\s+(?:app|application|tool)\b/i, "planner")
    .replace(/\btracking\s+(?:app|application|tool)\b/i, "tracker")
    .replace(/\bmanagement\s+(?:app|application|tool)\b/i, "manager")
    .replace(/\borganizing\s+(?:app|application|tool)\b/i, "organizer")
    .replace(/\bscheduling\s+(?:app|application|tool)\b/i, "scheduler")
    .replace(/\b(app|application|website|tool|platform)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  return title(phrase || "New App");
}

/** Once a run settles, keep only its durable transcript summary visible. */
export function transcriptMessages(
  messages: readonly ConversationMessage[],
  runs: readonly ConversationRun[],
): ConversationMessage[] {
  return projectVisibleConversationMessages(messages, runs);
}

/** A latest failure stays next to the composer until the user starts new work. */
export function attachedTerminalOutcome(
  snapshot: ConversationSnapshot,
  releases: readonly AgentRelease[] = [],
): ConversationOutcome | null {
  if (latestActiveRoot(snapshot.runs)) return null;
  const latestRoot = [...snapshot.runs]
    .reverse()
    .find((run) => run.parentRunId === null);
  if (!latestRoot || !["failed", "cancelled"].includes(latestRoot.status))
    return null;
  return (
    [...presentedOutcomes(snapshot.outcomes ?? [], snapshot.runs, releases)]
      .reverse()
      .find(
        (outcome) =>
          outcome.runId === latestRoot.id &&
          (outcome.status === "failed" || outcome.status === "cancelled"),
      ) ?? null
  );
}

/**
 * A strict harness can fail after it has already activated an exact release.
 * Reconcile that historical edge from immutable deployment timing so the UI
 * never tells a user that a live change was not released.
 */
export function presentedOutcomes(
  outcomes: readonly ConversationOutcome[],
  runs: readonly ConversationRun[],
  releases: readonly AgentRelease[],
): ConversationOutcome[] {
  const runById = new Map(runs.map((run) => [run.id, run]));
  return outcomes.map((outcome) => {
    const run = runById.get(outcome.runId);
    if (
      outcome.status !== "failed" ||
      run?.terminalErrorClass !== "harness_outcome_gate_failed"
    ) {
      return outcome;
    }
    const startedAt = Date.parse(run.startedAt ?? run.createdAt);
    const finishedAt = Date.parse(run.finishedAt ?? outcome.occurredAt);
    const release = releases
      .filter((candidate) => {
        if (candidate.kind !== "release" || !candidate.activatedAt)
          return false;
        const activatedAt = Date.parse(candidate.activatedAt);
        return activatedAt >= startedAt && activatedAt <= finishedAt;
      })
      .sort((left, right) =>
        (left.activatedAt ?? left.createdAt).localeCompare(
          right.activatedAt ?? right.createdAt,
        ),
      )
      .at(-1);
    if (!release) return outcome;
    const progressGate = {
      id: "progress-cadence",
      label: "Progress updates",
      status: "failed" as const,
      detail: "A progress update missed its expected timing window.",
    };
    return {
      ...outcome,
      status: "released",
      summary: `The requested change is live. ${agentBrand().productName} recorded a completion-check issue after the release.`,
      release: {
        deploymentId: release.id,
        status: "failed",
        reviews: release.reviews,
        gates: [
          ...release.gates.filter((gate) => gate.id !== progressGate.id),
          progressGate,
        ],
      },
    };
  });
}

export function releaseHeadline(
  deploymentId: string,
  releases: readonly AgentRelease[],
): "Your app is live" | "Your app is updated" {
  const published = releases
    .filter(
      (release) =>
        release.kind === "release" &&
        LIVE_RELEASE_STATES.has(release.state) &&
        Boolean(release.activatedAt),
    )
    .sort((left, right) =>
      (left.activatedAt ?? left.createdAt).localeCompare(
        right.activatedAt ?? right.createdAt,
      ),
    );
  const index = published.findIndex((release) => release.id === deploymentId);
  if (index >= 0)
    return index === 0 ? "Your app is live" : "Your app is updated";
  return published.length === 0 ? "Your app is live" : "Your app is updated";
}

export function latestActiveRoot(
  runs: readonly ConversationRun[],
): ConversationRun | null {
  return (
    [...runs]
      .reverse()
      .find(
        (run) =>
          run.parentRunId === null &&
          run.phase !== "finished" &&
          !SETTLED_RUN_STATES.has(run.status),
      ) ?? null
  );
}

/**
 * The root run doing work now. A message sent during that work waits in a
 * queued run behind it, so Stop ends the work in progress and the waiting
 * message then starts, rather than Stop discarding the newer message.
 */
export function currentWorkRoot(
  runs: readonly ConversationRun[],
): ConversationRun | null {
  const unsettled = [...runs]
    .reverse()
    .filter(
      (run) =>
        run.parentRunId === null &&
        run.phase !== "finished" &&
        !SETTLED_RUN_STATES.has(run.status),
    );
  return unsettled.find((run) => run.status !== "queued") ?? unsettled[0] ?? null;
}

export function canRetryOutcome(
  outcome: ConversationOutcome,
  runs: readonly ConversationRun[],
): boolean {
  if (outcome.status !== "failed") return false;
  const run = runs.find((candidate) => candidate.id === outcome.runId);
  return (run?.checkpointVersion ?? 0) > 0;
}

function shortProductPhrase(value: string): string {
  const words = value
    .replace(/[^\p{L}\p{N}' -]+/gu, " ")
    .replace(/\b(?:responsive|beautiful|modern|simple|basic|new)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 4);
  return words.join(" ").slice(0, 54).trim();
}

function title(value: string): string {
  return (
    value
      .split(/\s+/)
      .filter(Boolean)
      .map(
        (word) =>
          word.charAt(0).toLocaleUpperCase() +
          word.slice(1).toLocaleLowerCase(),
      )
      .join(" ") || "New App"
  );
}

function plural(count: number, singular: string, pluralValue: string): string {
  return count === 1 ? singular : pluralValue;
}
